import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';
import AppDownloadSection from './components/AppDownloadSection';

/*
 * /individuals — the audience page for a person looking for themselves.
 *
 * Renamed from GetStartedPage on 4 Sep. The file name had been misleading since
 * /getstarted became a redirect on 3 Sep, and /getstarted is now a separate
 * router page rather than this content under a second URL.
 *
 * The change from the old page is one section. Capture / Insights / Outcomes
 * came out: it was a thinner version of what /how-it-works now covers, and it
 * never told the reader what they actually get. "Three things, and one of them
 * is a person" replaces it, and closes the gap the 4 Sep audit found — this was
 * the only landing page linking to neither product.
 *
 * #employer is load-bearing: /faq, /contact and the confirmation email that goes
 * to every individual enquirer all link to it.
 */

const IndividualsPage = () => {
    useEffect(() => {
        document.title = 'Get started | PhysioActivity';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', 'A registered physiotherapist builds you a programme from how you actually move, and it keeps changing as you do.');
    }, []);

    return (
    <div className="pa2">

        <section className="hero" id="top">
            <div className="wrap two">
                <div className="heromain">
                    <h1 className="on-dark">
                        <span className="setup">Your watch can&rsquo;t tell you what to do.</span>
                        <span className="payoff">A physio can.</span>
                    </h1>
                    <p className="lede">
                        Steps, workouts, all that data, and no plan. A registered
                        physiotherapist reads how you actually load your week, as{' '}
                        <Link to="/how-it-works"><b>Load Rate (LRE)</b></Link>, and writes you
                        one.
                    </p>
                    <p className="nerve">
                        You don&rsquo;t need to own a watch. The phone in your pocket is enough to
                        start.
                    </p>
                    <div className="cta-row">
                        <a
                            className="btn btn-t"
                            href="#get"
                            onClick={() => firePAEvent('app_download_click', { source: 'hero' })}
                        >
                            Get the app
                        </a>
                        <a className="btn btn-o" href="#employer">Is my employer covered?</a>
                    </div>
                </div>

                <div className="herovis">
                    {/* The production phone visual, reused rather than redrawn. It is a
                        finished asset that reads as a product screen; the watch-plus-phone
                        cluster built for QA never did.
                        NOTE: it labels the metric "Load Rate" twice. That is a product
                        label on a screen, not the "our digital biomarker" construction
                        QA-05 removed — but it is the same words, so it is worth the CMO
                        seeing it rather than me deciding it is fine. */}
                    <svg
                        className="hero-phone"
                        viewBox="0 0 300 600"
                        role="img"
                        aria-label="The PhysioActivity app, showing a movement summary"
                    >
                        <rect x="8" y="8" width="284" height="584" rx="44" fill="#0c2c4d" />
                        <rect x="20" y="20" width="260" height="560" rx="34" fill="#ffffff" />
                        <rect x="116" y="30" width="68" height="13" rx="6" fill="#0c2c4d" />
                        <text x="40" y="86" fontFamily="Inter,sans-serif" fontSize="13" fill="#5b6b7d" fontWeight="600">Movement today</text>
                        <text x="40" y="112" fontFamily="Inter,sans-serif" fontSize="22" fill="#104378" fontWeight="800">Good progress</text>
                        <g transform="translate(150,215)">
                            <circle r="64" fill="none" stroke="#E8F4F6" strokeWidth="16" />
                            <circle r="64" fill="none" stroke="#4CB6C3" strokeWidth="16" strokeLinecap="round" strokeDasharray="402" strokeDashoffset="100" transform="rotate(-90)" />
                            <text x="0" y="4" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="40" fontWeight="800" fill="#104378">82</text>
                            <text x="0" y="28" textAnchor="middle" fontFamily="Inter,sans-serif" fontSize="13" fill="#5b6b7d">Load Rate</text>
                        </g>
                        <g fontFamily="Inter,sans-serif">
                            <rect x="36" y="305" width="100" height="64" rx="14" fill="#f6fafb" stroke="#e3eaf0" />
                            <text x="52" y="334" fontSize="20" fontWeight="800" fill="#104378">8,432</text>
                            <text x="52" y="354" fontSize="11" fill="#5b6b7d">steps</text>
                            <rect x="164" y="305" width="100" height="64" rx="14" fill="#f6fafb" stroke="#e3eaf0" />
                            <text x="180" y="334" fontSize="20" fontWeight="800" fill="#104378">81</text>
                            <text x="180" y="354" fontSize="11" fill="#5b6b7d">Load Rate</text>
                        </g>
                        <text x="40" y="412" fontFamily="Inter,sans-serif" fontSize="11" fill="#5b6b7d" fontWeight="600">THIS WEEK</text>
                        <g fill="#4CB6C3">
                            <rect x="40" y="478" width="22" height="40" rx="5" />
                            <rect x="74" y="460" width="22" height="58" rx="5" />
                            <rect x="108" y="438" width="22" height="80" rx="5" />
                            <rect x="142" y="452" width="22" height="66" rx="5" fill="#104378" />
                            <rect x="176" y="428" width="22" height="90" rx="5" />
                            <rect x="210" y="466" width="22" height="52" rx="5" />
                            <rect x="244" y="444" width="22" height="74" rx="5" />
                        </g>
                    </svg>
                    <p className="cap">Illustrative. Not a real user.</p>
                </div>
            </div>
        </section>

        <section className="tint" id="why">
            <div className="wrap">
                <h2>
                    <span className="setup">You don&rsquo;t need an injury</span>
                    <span className="payoff">to see a physiotherapist.</span>
                </h2>
                <p className="lede">
                    Most people think of physio as something for after an injury. But movement is
                    personal, the same way nutrition is. You might see a nutritionist to eat
                    better, not because something is wrong.
                </p>

                <div className="cards">
                    <div className="card">
                        <h3>Ahead of the twinges</h3>
                        <p>Understand how you move and act on it early, rather than after something has already stopped you.</p>
                    </div>
                    <div className="card">
                        <h3>A professional opinion</h3>
                        <p>A qualified view on how you move, the same way you would get one on how you eat or train.</p>
                    </div>
                    <div className="card">
                        <h3>Built around you</h3>
                        <p>Movement is individual, like metabolism. Guidance fits your body, your goals and your life.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* Replaces Capture / Insights / Outcomes, which duplicated /how-it-works
            and never said what the reader actually gets. Every card here is also a
            route out: this was the only landing page linking to neither product. */}
        <section className="white" id="what">
            <div className="wrap">
                <h2>
                    <span className="setup">Three things,</span>
                    <span className="payoff">and one of them is a person.</span>
                </h2>
                <p className="lede">
                    There is an app, a portal and a physiotherapist. You will use the first, you
                    can look at the second whenever you want, and the third is who decides.
                </p>

                <div className="cards">
                    <div className="card">
                        <h3><Link to="/steadystride">SteadyStride</Link></h3>
                        <p>The app. Your programme, and a <b>smart diary</b> of sessions, notes and what you actually did. Connect a watch and it brings heart rate and sleep with it.</p>
                    </div>
                    <div className="card">
                        <h3><Link to="/cmpx">CMPX</Link></h3>
                        <p>The portal. Everything gathered into <b>one record</b>, and the place you set who can see it. Your physiotherapist works in the same portal, on their part of it.</p>
                    </div>
                    <div className="card">
                        <h3><Link to="/how-it-works">A named physiotherapist</Link></h3>
                        <p>Registered, named in the app with their number. The software proposes; <b>they decide</b>, and that decision is recorded against them.</p>
                    </div>
                </div>

                <p className="quiet">
                    You can also open your record to a clinician who is not on the platform,
                    with a <Link to="/steadystride#share">Sharing Code</Link>, read-only and for
                    a limited time.{' '}
                    <Link to="/blog/who-writes-your-programme">Who writes your programme</Link>.
                </p>
            </div>
        </section>

        {/* #employer — the footer and the contact form both link here. */}
        <section className="tint" id="employer">
            <div className="wrap">
                <h2><span className="setup">Is my employer covered?</span></h2>
                <p className="lede">
                    Some people get SteadyStride through work, at no cost to them. If yours
                    provides it, you will have had an invitation with a code.
                </p>

                <div className="contrast">
                    <div className="cc yes">
                        <h3>If your employer covers it</h3>
                        <ul>
                            <li>Enter the code from your invitation when you open the app</li>
                            <li>It costs you nothing</li>
                            <li>Your employer never sees your data, your programme or your name</li>
                            <li>The account stays yours if you leave</li>
                        </ul>
                    </div>
                    <div className="cc alt">
                        <h3>If they do not, yet</h3>
                        <ul>
                            <li>You can still download the app and start</li>
                            <li>Tell us who you work for and we will approach them</li>
                            <li>Or send them the cost estimate yourself</li>
                        </ul>
                    </div>
                </div>

                <div className="cta-row">
                    <Link className="btn btn-t" to="/contact?who=individual">Ask us to approach your employer</Link>
                </div>
                <p className="quiet">
                    Choose &ldquo;asking for myself&rdquo; on the form. What an employer can and
                    cannot see is set out in the{' '}
                    <Link to="/trust-centre">trust centre</Link>.
                </p>
            </div>
        </section>

        <section className="s-promise" id="get">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Start where you are.</span>
                    <span className="payoff">Not where you should be.</span>
                </h2>
                <p className="lede">
                    Your first programme is built from how you actually move this week, not from a
                    questionnaire about how you usually feel.
                </p>
                <AppDownloadSection appId="steadystride" />
            </div>
        </section>

    </div>
    );
};

export default IndividualsPage;
